<template>
  <div class="thread-container">
    <!-- Enhanced animated background -->
    <div class="thread-background">
      <div class="floating-particles">
        <div class="particle particle-1"></div>
        <div class="particle particle-2"></div>
        <div class="particle particle-3"></div>
        <div class="particle particle-4"></div>
        <div class="particle particle-5"></div>
      </div>
      <div class="gradient-overlay"></div>
    </div>

    <div
      class="thread-page flex flex-col flex-1 h-full w-full min-h-0"
      :class="{ 'desktop-fixed-height': !isSmallScreen }"
      style="
        background: none !important;
        box-shadow: none !important;
        border: none !important;
        margin: 0;
        padding: 0;
      "
    >
      <div
        class="thread-main-card glass-card flex-1 flex flex-col p-0 md:p-0 mt-0 mb-0 relative overflow-hidden"
      >
        <!-- Enhanced Header with glassmorphism -->
        <div
          class="thread-header flex items-center justify-between px-4 py-4 border-b border-border sticky top-0 z-20 glass-header bg-card"
        >
          <div class="flex items-center gap-4">
            <!-- Enhanced Avatar - Always shown -->
            <div class="avatar-wrapper relative">
              <div
                v-if="isAdminProfile()"
                class="w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold text-white border-2 border-border avatar-main bg-blue-500"
              >
                <img
                  src="/logo/applypa-logo.png"
                  alt="Admin"
                  class="w-full h-full object-cover rounded-full"
                />
              </div>
              <div
                v-else
                class="w-14 h-14 rounded-full bg-blue-500 flex items-center justify-center text-2xl font-bold text-white border-2 border-border avatar-main"
              >
                {{ getUserInitial() }}
              </div>
            </div>
            <div class="participant-info">
              <h1 class="text-2xl font-bold text-foreground tracking-tight">
                {{ isAdminProfile() ? "Admin" : getUserName() }}
              </h1>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <!-- Archive button -->
            <button
              @click="archiveCurrentThread"
              :disabled="loading"
              class="archive-btn w-12 h-12 flex items-center justify-center rounded-2xl glass-card hover:scale-105 transition-all duration-300 focus:outline-none relative group"
              :title="isCurrentThreadArchived ? 'Unarchive' : 'Archive'"
            >
              <svg
                class="h-6 w-6 text-muted-foreground"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                <path
                  fill-rule="evenodd"
                  d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <span
                class="tooltip absolute -bottom-10 left-1/2 -translate-x-1/2 bg-black/80 text-white text-xs rounded-lg px-3 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                >{{ isCurrentThreadArchived ? "Unarchive" : "Archive" }}</span
              >
            </button>

            <!-- Enhanced refresh button -->
            <button
              @click="fetchMessages"
              :disabled="loading"
              class="refresh-btn w-12 h-12 flex items-center justify-center rounded-2xl glass-card hover:scale-105 transition-all duration-300 focus:outline-none relative group"
              title="Refresh"
            >
              <svg
                v-if="loading"
                class="animate-spin h-6 w-6 text-muted-foreground"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                ></path>
              </svg>
              <svg
                v-else
                class="h-6 w-6 text-muted-foreground group-hover:rotate-180 transition-transform duration-300"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582M20 20v-5h-.581M5 19a9 9 0 0014 0M5 5a9 9 0 0114 0"
                />
              </svg>
              <span
                class="tooltip absolute -bottom-10 left-1/2 -translate-x-1/2 bg-black/80 text-white text-xs rounded-lg px-3 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                >Refresh</span
              >
            </button>
          </div>
        </div>

        <!-- Enhanced Message List -->
        <div
          v-if="loading"
          class="loading-state flex-1 flex items-center justify-center p-12"
        >
          <div class="loading-animation">
            <div class="loading-dots">
              <div class="dot dot-1"></div>
              <div class="dot dot-2"></div>
              <div class="dot dot-3"></div>
            </div>
            <p class="text-white/70 mt-4 text-lg">Loading messages...</p>
          </div>
        </div>

        <div v-else class="flex-1 flex flex-col overflow-hidden min-h-0">
          <!-- Enhanced Empty State -->
          <div
            v-if="messages.length === 0"
            class="empty-messages-state flex-1 flex items-center justify-center flex-col text-center p-12"
          >
            <div class="empty-icon-wrapper mb-6">
              <div class="empty-icon-bg"></div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-20 w-20 text-white/40 relative z-10"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <h3 class="text-2xl font-semibold text-white mb-3">
              No messages yet
            </h3>
            <p class="text-white/60 text-lg">
              Start the conversation by sending a message below
            </p>
          </div>

          <!-- Enhanced Messages Container -->
          <div
            v-else
            class="messages-container flex-1 overflow-y-auto px-4 py-4"
            ref="messagesContainer"
            @scroll="onScroll"
            style="max-height: none; min-height: 200px"
          >
            <transition-group name="message-slide" tag="div">
              <template v-for="(msg, index) in filteredMessages" :key="msg.id">
                <!-- Enhanced Date Separator -->
                <div
                  v-if="shouldShowDateSeparator(msg, index)"
                  class="date-separator w-full text-center my-8"
                >
                  <div
                    class="date-badge inline-block px-6 py-2 glass-card text-sm text-white font-semibold"
                  >
                    {{ formatDateSeparator(msg.sent_at) }}
                  </div>
                </div>

                <!-- Enhanced Message Row -->
                <div
                  class="message-row"
                  :class="[
                    'flex',
                    isMessageFromCurrentUser(msg)
                      ? 'justify-end'
                      : 'justify-start',
                    shouldShowAvatar(msg, index) ? 'mb-6' : 'mb-2',
                  ]"
                >
                  <div
                    class="flex items-end max-w-full group relative message-group"
                  >
                    <!-- Enhanced Avatar for other users -->
                    <div
                      v-if="
                        !isMessageFromCurrentUser(msg) &&
                        shouldShowAvatar(msg, index)
                      "
                      class="message-avatar w-12 h-12 rounded-full flex items-center justify-center mr-3 text-lg font-bold text-white border-2 border-border bg-blue-500"
                      :aria-label="msg.sender_name"
                      tabindex="0"
                    >
                      <img
                        v-if="isAdmin(msg)"
                        src="/logo/applypa-logo.png"
                        alt="Admin"
                        class="w-full h-full object-cover rounded-full"
                      />
                      <template v-else>{{
                        msg.sender_name.charAt(0).toUpperCase()
                      }}</template>
                      <div
                        v-if="showTooltip === msg.id"
                        class="avatar-tooltip absolute bottom-full mb-2 left-0 bg-black/80 text-white text-xs px-3 py-1 rounded-lg whitespace-nowrap"
                      >
                        {{ isAdmin(msg) ? "Admin" : msg.sender_name }}
                      </div>
                    </div>

                    <!-- Spacer for consecutive messages -->
                    <div
                      v-else-if="
                        !isMessageFromCurrentUser(msg) &&
                        !shouldShowAvatar(msg, index)
                      "
                      class="w-12 mr-3"
                    ></div>

                    <!-- Enhanced Message Bubble -->
                    <div
                      :class="[
                        'message-bubble relative px-4 py-3 rounded-3xl shadow-lg break-words',
                        isMessageFromCurrentUser(msg)
                          ? 'message-from-me ml-8'
                          : 'message-from-them mr-8',
                        isMessageFromCurrentUser(msg)
                          ? 'bg-blue-500 text-white'
                          : 'bg-muted text-foreground',
                        'transition-all duration-300 hover:scale-[1.02] hover:shadow-xl',
                        isSmallScreen
                          ? 'max-w-[75vw] sm:max-w-[250px]'
                          : 'max-w-xs md:max-w-md',
                      ]"
                      style="min-width: 120px"
                      :aria-label="'Message from ' + msg.sender_name"
                      tabindex="0"
                    >
                      <!-- Enhanced Reply Context -->
                      <transition name="fade">
                        <div
                          v-if="msg.reply_to && repliedMessageMap[msg.reply_to]"
                          class="reply-context mb-3 px-4 py-3 rounded-2xl bg-white/10 border-l-4 border-white/30 animate-fadeIn"
                        >
                          <div class="reply-header flex items-center mb-1">
                            <svg
                              class="w-4 h-4 mr-2 text-foreground"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                clip-rule="evenodd"
                              />
                            </svg>
                            <span
                              v-if="!isAdmin(repliedMessageMap[msg.reply_to])"
                              class="font-semibold text-foreground text-sm"
                              >{{
                                repliedMessageMap[msg.reply_to].sender_name
                              }}</span
                            >
                            <span
                              v-else
                              :class="[
                                'admin-badge-reply px-2 py-0.5 text-xs font-bold flex items-center',
                                'text-foreground', // Always readable in reply context
                              ]"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                class="h-3.5 w-3.5 mr-1 text-foreground"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                stroke-width="2"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                                />
                              </svg>
                              Admin
                            </span>
                          </div>
                          <p class="text-white/70 text-sm truncate">
                            {{ repliedMessageMap[msg.reply_to].body }}
                          </p>
                        </div>
                      </transition>

                      <!-- Enhanced Message Header -->
                      <div
                        class="message-header mb-2 flex items-center justify-between"
                      >
                        <div class="sender-info flex items-center">
                          <span class="font-semibold text-sm">
                            {{ isAdmin(msg) ? "" : msg.sender_name }}
                          </span>
                          <span
                            v-if="isAdmin(msg)"
                            :class="[
                              'admin-badge ml-0 px-2 py-1 text-xs font-bold flex items-center',
                              isMessageFromCurrentUser(msg)
                                ? 'text-white'
                                : 'text-foreground',
                            ]"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              :class="[
                                'h-3.5 w-3.5 mr-1',
                                isMessageFromCurrentUser(msg)
                                  ? 'text-white'
                                  : 'text-foreground',
                              ]"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              stroke-width="2"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                              />
                            </svg>
                            Admin
                          </span>
                        </div>
                      </div>

                      <!-- Message Content -->
                      <div
                        v-if="editingMessageId !== msg.id"
                        class="message-content text-base leading-relaxed break-words overflow-hidden"
                      >
                        {{ msg.body }}
                      </div>
                      <div
                        v-else
                        class="message-edit-form"
                        style="
                          position: relative;
                          z-index: 50;
                          background: hsl(var(--card) / 0.95);
                          padding: 12px;
                          border-radius: 16px;
                          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
                        "
                      >
                        <textarea
                          v-model="editMessageBody"
                          class="w-full p-3 rounded-lg border border-blue-500/30 focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30 text-foreground bg-muted placeholder:text-muted-foreground transition-all duration-200"
                          rows="2"
                          style="
                            resize: vertical;
                            min-height: 48px;
                            font-size: 1rem;
                          "
                          :disabled="false"
                          placeholder="Edit your message..."
                        ></textarea>
                        <div class="flex gap-2 mt-3">
                          <button
                            @click="saveEditMessage(msg)"
                            class="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow transition-all duration-150"
                          >
                            Save
                          </button>
                          <button
                            @click="cancelEditMessage"
                            class="px-4 py-2 rounded-lg border border-border bg-card text-foreground hover:bg-accent font-semibold shadow-sm transition-all duration-150"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>

                      <!-- Enhanced Message Footer -->
                      <div
                        class="message-footer flex items-center justify-between mt-3 pt-2 border-t border-white/10"
                      >
                        <span class="message-time text-xs opacity-70">
                          {{ formatTime(msg.sent_at) }}
                        </span>
                        <div
                          class="message-status flex items-center"
                          v-if="isMessageFromCurrentUser(msg)"
                        >
                          <svg
                            v-if="msg.is_read"
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-4 w-4 text-green-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fill-rule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clip-rule="evenodd"
                            />
                          </svg>
                          <svg
                            v-else
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-4 w-4 text-white/50"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 110-12 6 6 0 010 12z"
                            />
                          </svg>
                        </div>
                      </div>

                      <!-- Enhanced Message Actions -->
                      <div
                        class="message-actions absolute top-2 right-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200"
                      >
                        <button
                          @click="toggleMessageMenu(msg.id)"
                          aria-label="Message actions"
                          class="action-menu-btn p-2 rounded-full glass-card hover:scale-110 transition-transform duration-200 focus:ring-2 focus:ring-white/50"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-4 w-4 text-white"
                            fill="none"
                            viewBox="0 0 20 20"
                            stroke="currentColor"
                          >
                            <circle cx="4" cy="10" r="1.5" />
                            <circle cx="10" cy="10" r="1.5" />
                            <circle cx="16" cy="10" r="1.5" />
                          </svg>
                        </button>

                        <!-- Enhanced Actions Dropdown -->
                        <div
                          v-if="activeMessageMenu === msg.id"
                          class="actions-dropdown absolute right-0 mt-2 w-36 glass-card border border-white/20 shadow-xl z-50 rounded-2xl overflow-hidden"
                        >
                          <button
                            @click="
                              startReply(msg);
                              closeMessageMenu();
                            "
                            class="dropdown-action w-full text-left px-4 py-3 text-sm text-white hover:bg-white/10 transition-colors flex items-center"
                          >
                            <svg
                              class="w-4 h-4 mr-3"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                clip-rule="evenodd"
                              />
                            </svg>
                            Reply
                          </button>
                          <button
                            v-if="isMessageFromCurrentUser(msg)"
                            @click="
                              editMessage(msg);
                              closeMessageMenu();
                            "
                            class="dropdown-action w-full text-left px-4 py-3 text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-500/10 transition-colors flex items-center"
                          >
                            <svg
                              class="w-4 h-4 mr-3"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                d="M17.414 2.586a2 2 0 00-2.828 0l-9.9 9.9A2 2 0 004 14v2a2 2 0 002 2h2a2 2 0 001.414-.586l9.9-9.9a2 2 0 000-2.828l-2-2zM5 16v-2.586l9-9L16.586 7l-9 9H5z"
                              />
                            </svg>
                            Edit
                          </button>
                          <button
                            v-if="isMessageFromCurrentUser(msg)"
                            @click="
                              deleteMessage(msg);
                              closeMessageMenu();
                            "
                            class="dropdown-action w-full text-left px-4 py-3 text-sm text-red-700 dark:text-red-300 hover:bg-red-500/10 transition-colors flex items-center"
                          >
                            <svg
                              class="w-4 h-4 mr-3"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"
                                clip-rule="evenodd"
                              />
                              <path
                                fill-rule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clip-rule="evenodd"
                              />
                            </svg>
                            Unsend
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Enhanced Avatar for current user -->
                    <div
                      v-if="
                        isMessageFromCurrentUser(msg) &&
                        shouldShowAvatar(msg, index)
                      "
                      class="message-avatar w-12 h-12 rounded-full flex items-center justify-center ml-3 text-lg font-bold text-white border-2 border-border bg-blue-500"
                      :aria-label="msg.sender_name"
                      tabindex="0"
                    >
                      <img
                        v-if="user && user.account_type === 'admin'"
                        src="/logo/applypa-logo.png"
                        alt="Admin"
                        class="w-full h-full object-cover rounded-full"
                      />
                      <template v-else>{{
                        msg.sender_name.charAt(0).toUpperCase()
                      }}</template>
                      <div
                        v-if="showTooltip === msg.id + '-user'"
                        class="avatar-tooltip absolute bottom-full mb-2 right-0 bg-black/80 text-white text-xs px-3 py-1 rounded-lg whitespace-nowrap"
                      >
                        {{
                          user && user.account_type === "admin"
                            ? "Admin"
                            : msg.sender_name
                        }}
                      </div>
                    </div>

                    <!-- Spacer for consecutive messages -->
                    <div
                      v-else-if="
                        isMessageFromCurrentUser(msg) &&
                        !shouldShowAvatar(msg, index)
                      "
                      class="w-12 ml-3"
                    ></div>
                  </div>
                </div>
              </template>
            </transition-group>
          </div>

          <!-- Enhanced Reply Preview -->
          <transition name="slide-up">
            <div
              v-if="replyToMessage"
              class="reply-preview flex items-center gap-3 mx-4 mb-2 p-3 glass-card border-l-4 border-blue-400 animate-fadeIn"
            >
              <div class="reply-icon">
                <svg
                  class="w-5 h-5 text-blue-600 dark:text-blue-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
              <div class="flex-1">
                <div class="text-sm text-white/90 font-semibold">
                  Replying to
                  <span v-if="!isAdmin(replyToMessage)">{{
                    replyToMessage.sender_name
                  }}</span>
                  <span
                    v-else
                    class="admin-badge-reply inline-flex items-center px-2 py-0.5 ml-1 text-foreground text-xs font-bold"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-3.5 w-3.5 mr-1 text-foreground"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                    Admin
                  </span>
                </div>
                <div class="truncate text-sm text-white/70 max-w-xs">
                  {{ replyToMessage.body }}
                </div>
              </div>
              <button
                @click="cancelReply"
                class="cancel-reply-btn p-2 rounded-full hover:bg-white/10 transition-colors"
                title="Cancel reply"
                aria-label="Cancel reply"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-white/70 hover:text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </transition>

          <!-- Enhanced Input Area -->
          <form
            v-if="canReply"
            @submit.prevent="sendReply"
            class="input-area mt-2 border-t border-white/10 pt-4 sticky bottom-0 glass-header z-10"
          >
            <div class="input-container flex items-center gap-4 px-4 pb-4">
              <div class="flex-1 relative">
                <textarea
                  v-model="replyBody"
                  @focus="onFocus"
                  @blur="onBlur"
                  placeholder="Type your message..."
                  class="message-input w-full border-0 p-4 rounded-none focus:ring-2 focus:ring-blue-400/50 resize-none min-h-[56px] max-h-32 glass-card text-white placeholder-white/60 transition-all duration-300"
                  :rows="1"
                  ref="replyInput"
                  @keydown.enter.exact.prevent="sendReply"
                  required
                  aria-label="Type your message"
                ></textarea>
              </div>

              <!-- Enhanced Send Button -->
              <button
                type="submit"
                :disabled="!replyBody.trim() || sendingMessage"
                :class="[
                  'send-button w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-400/50 flex-shrink-0',
                  replyBody.trim() && !sendingMessage
                    ? 'bg-blue-500 hover:bg-blue-600 hover:scale-105 cursor-pointer'
                    : 'bg-muted/60 cursor-not-allowed opacity-50',
                ]"
                aria-label="Send message"
              >
                <template v-if="sendingMessage">
                  <svg
                    class="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    ></path>
                  </svg>
                </template>
                <template v-else>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                    class="text-white"
                  >
                    <path
                      d="M3 20.5L21 12 3 3.5 3 10.5 17 12 3 13.5 3 20.5Z"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </template>
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Enhanced Unsend Confirmation Modal -->
      <transition name="modal-fade">
        <div
          v-if="showUnsendModal"
          class="modal-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
        >
          <div
            class="modal-content glass-card max-w-sm w-full mx-4 p-8 relative animate-fadeIn rounded-3xl border border-white/20"
          >
            <button
              @click="closeUnsendModal"
              class="modal-close absolute top-4 right-4 text-white/60 hover:text-white focus:outline-none transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div class="modal-body flex flex-col items-center text-center">
              <div class="modal-icon bg-red-500/20 rounded-full p-4 mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-8 w-8 text-red-600 dark:text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h2 class="text-xl font-bold mb-3 text-white">Unsend Message?</h2>
              <p class="text-white/70 mb-6 text-sm leading-relaxed">
                This will permanently delete the message for everyone. This
                action cannot be undone.
              </p>

              <div class="modal-actions flex gap-3 w-full">
                <button
                  @click="closeUnsendModal"
                  class="flex-1 py-3 rounded-2xl glass-card text-white font-semibold hover:bg-white/10 transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  @click="confirmUnsend"
                  class="flex-1 py-3 rounded-2xl bg-red-500 text-white font-semibold hover:bg-red-600 transition-all duration-300 shadow-lg"
                >
                  Unsend
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- Message Sending Loading Modal -->
      <transition name="modal-fade">
        <div
          v-if="sendingMessage"
          class="modal-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
        >
          <div
            class="modal-content glass-card max-w-sm w-full mx-4 p-6 md:p-8 relative animate-fadeIn rounded-3xl border border-white/20 shadow-xl"
          >
            <div class="modal-body flex flex-col items-center text-center">
              <div class="sending-animation mb-5 md:mb-6">
                <div class="sending-circle"></div>
                <div class="sending-circle"></div>
                <div class="sending-circle"></div>
                <div class="sending-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    fill="none"
                    viewBox="0 0 24 24"
                    class="text-blue-600 dark:text-blue-400 animate-pulse"
                  >
                    <path
                      d="M3 20.5L21 12 3 3.5 3 10.5 17 12 3 13.5 3 20.5Z"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
              </div>
              <h2 class="text-xl font-bold mb-2 md:mb-3 text-white">
                Sending Message
              </h2>
              <p class="text-white/70 mb-1 md:mb-2 text-sm leading-relaxed">
                Your message is being sent. Please wait a moment...
              </p>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from "vue";
import apiClient from "@/services/api";
import authStore from "@/store/auth";

export default {
  name: "MessageThread",
  props: {
    threadId: {
      type: [String, Number],
      required: true,
    },
  },
  setup(props) {
    const messages = ref([]);
    const loading = ref(true);
    const replyBody = ref("");
    const replyToMessage = ref(null);
    const user = computed(() => authStore.state.user);
    const canReply = computed(() => !!user.value);
    const filteredMessages = computed(() =>
      (messages.value || []).filter((msg) => !!msg && !!msg.id)
    );
    const isTyping = ref(false);
    const messagesContainer = ref(null);
    const replyInput = ref(null);
    const showTooltip = ref(null);
    const showEmojiPicker = ref(false);
    const threadAdminId = ref(null);
    const repliedMessageMap = computed(() => {
      const map = {};
      for (const msg of messages.value) {
        map[msg.id] = msg;
      }
      return map;
    });
    const showUnsendModal = ref(false);
    const messageToUnsend = ref(null);
    const floatingDate = ref(null);
    const activeMessageMenu = ref(null);
    const isNearBottom = ref(true);
    const scrollPosition = ref(0);
    const isSmallScreen = ref(window.innerWidth <= 1024);
    const sendingMessage = ref(false);
    const editingMessageId = ref(null);
    const editMessageBody = ref("");

    // Defensive alignment helper
    const isMessageFromCurrentUser = (msg) => {
      return user.value && msg && msg.sender === user.value.id;
    };

    // Computed to get the name of the other participant
    const otherParticipant = computed(() => {
      if (!user.value || !messages.value.length) return "";
      // Find a message not from the current user
      const otherMsg = messages.value.find(
        (msg) => msg && msg.sender !== user.value.id
      );
      return otherMsg ? otherMsg.sender_name : "";
    });

    const markThreadRead = async () => {
      try {
        await apiClient.post(`/messages/threads/${props.threadId}/mark_read/`);
      } catch (err) {
        // Silently ignore errors for marking as read
      }
    };

    const threadInfo = ref(null);

    const fetchThreadInfo = async () => {
      const res = await apiClient.get(`/messages/threads/${props.threadId}/`);
      threadAdminId.value = res.data.admin;
      threadInfo.value = res.data;
    };

    // Check if current thread is archived
    const isCurrentThreadArchived = computed(() => {
      if (!threadInfo.value || !user.value) return false;
      if (user.value.account_type === "jobseeker") {
        return threadInfo.value.is_archived_by_jobseeker;
      }
      return threadInfo.value.is_archived_by_admin;
    });

    // Archive/Unarchive current thread
    const archiveCurrentThread = async () => {
      try {
        await apiClient.post(`/messages/threads/${props.threadId}/archive/`);
        await fetchThreadInfo(); // Refresh thread info
        // Emit event to parent to refresh thread list
        window.dispatchEvent(new CustomEvent("thread-archived"));
      } catch (err) {
        console.error("Archive error:", err.response?.data || err);
        alert(
          "Error: " +
            (err.response?.data?.detail ||
              JSON.stringify(err.response?.data) ||
              err.message)
        );
      }
    };

    const fetchMessages = async () => {
      loading.value = true;
      // Save scroll position before fetching new messages
      if (messagesContainer.value) {
        scrollPosition.value = messagesContainer.value.scrollTop;
      }

      const res = await apiClient.get(
        `/messages/threads/${props.threadId}/messages/`
      );
      const arr = Array.isArray(res.data) ? res.data : res.data.results || [];
      messages.value = arr.filter((msg) => !!msg && !!msg.id);
      loading.value = false;
      markThreadRead();

      // After updating messages, restore scroll position
      nextTick(() => {
        if (messagesContainer.value) {
          if (isNearBottom.value) {
            scrollToBottom();
          } else {
            restoreScrollPosition();
          }
        }
      });
    };

    const sendReply = async () => {
      // Don't send if empty message or already sending
      if (!replyBody.value.trim() || sendingMessage.value) return;

      const payload = { body: replyBody.value };
      if (replyToMessage.value) {
        payload.reply_to = replyToMessage.value.id;
      }

      // Store message content in case we need to retry
      const messageContent = replyBody.value;

      // Check if user is near bottom before sending
      updateIsNearBottom();

      // Clear input immediately for better UX
      replyBody.value = "";

      try {
        // Show loading state
        sendingMessage.value = true;

        // Send the message
        await apiClient.post(
          `/messages/threads/${props.threadId}/messages/`,
          payload
        );

        // Clear related states after successful send
        replyToMessage.value = null;
        showEmojiPicker.value = false;

        // Fetch updated messages
        await fetchMessages();
        focusReplyInput();
      } catch (err) {
        console.error("Reply error:", err.response?.data || err);

        // Restore message content if sending failed
        replyBody.value = messageContent;

        // Show error message
        alert(
          "Error: " +
            (err.response?.data?.detail ||
              JSON.stringify(err.response?.data) ||
              err.message)
        );
      } finally {
        // Hide loading state
        sendingMessage.value = false;
      }
    };

    const startReply = (msg) => {
      replyToMessage.value = msg;
      focusReplyInput();
    };
    const cancelReply = () => {
      replyToMessage.value = null;
    };

    const deleteMessage = (msg) => {
      messageToUnsend.value = msg;
      showUnsendModal.value = true;
    };

    const closeUnsendModal = () => {
      showUnsendModal.value = false;
      messageToUnsend.value = null;
    };

    const confirmUnsend = async () => {
      if (!messageToUnsend.value) return;

      // Check if user is near bottom before unsending
      updateIsNearBottom();

      try {
        await apiClient.delete(
          `/messages/messages/${messageToUnsend.value.id}/`
        );
        await fetchMessages();
      } catch (err) {
        alert(
          "Failed to unsend: " + (err.response?.data?.detail || err.message)
        );
      } finally {
        closeUnsendModal();
      }
    };

    const focusReplyInput = () => {
      nextTick(() => {
        if (replyInput.value) {
          replyInput.value.focus();
        }
      });
    };

    const formatDate = (dateStr) => {
      return new Date(dateStr).toLocaleString();
    };

    // Format time to only show the time component (e.g., "3:45 PM")
    const formatTime = (dateStr) => {
      const date = new Date(dateStr);
      return date.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      });
    };

    // Format date for separator (Today, Yesterday, or date)
    const formatDateSeparator = (dateStr) => {
      const date = new Date(dateStr);
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      if (date.toDateString() === today.toDateString()) {
        return "Today";
      } else if (date.toDateString() === yesterday.toDateString()) {
        return "Yesterday";
      } else {
        return date.toLocaleDateString([], {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      }
    };

    // Check if we should show a date separator before this message
    const shouldShowDateSeparator = (msg, index) => {
      if (index === 0) return true;

      const prevMsg = filteredMessages.value[index - 1];
      const msgDate = new Date(msg.sent_at);
      const prevDate = new Date(prevMsg.sent_at);

      return msgDate.toDateString() !== prevDate.toDateString();
    };

    const onFocus = () => {
      isTyping.value = true;
      showEmojiPicker.value = false;
    };
    const onBlur = () => {
      isTyping.value = false;
    };

    const scrollToBottom = () => {
      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop =
            messagesContainer.value.scrollHeight;
        }
      });
    };

    const restoreScrollPosition = () => {
      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop = scrollPosition.value;
        }
      });
    };

    // Check if user is near the bottom of the scroll area
    const updateIsNearBottom = () => {
      if (!messagesContainer.value) return;

      const container = messagesContainer.value;
      const scrollBottom = container.scrollTop + container.clientHeight;
      const threshold = 100; // pixels from bottom to consider "near bottom"

      isNearBottom.value = scrollBottom >= container.scrollHeight - threshold;
      scrollPosition.value = container.scrollTop;
    };

    // Only scroll to bottom on initial load
    const initialScrollToBottom = () => {
      // Only auto-scroll on first load
      if (loading.value === false && messages.value.length > 0) {
        scrollToBottom();
      }
    };

    // Watch for messages only on initial load
    watch(
      () => loading.value,
      (newVal, oldVal) => {
        if (oldVal === true && newVal === false) {
          initialScrollToBottom();
        }
      }
    );

    // Helper to check if a message is from the admin
    const isAdmin = (msg) => {
      return msg.sender_account_type === "admin";
    };

    // Helper to check if we should show admin profile
    const isAdminProfile = () => {
      // For jobseeker users, show admin profile if the other user is admin
      if (user.value?.account_type === "jobseeker") {
        return threadInfo.value?.admin !== null;
      }
      return false;
    };

    // Helper to determine if we should show avatar for this message
    const shouldShowAvatar = (msg, index) => {
      if (index === 0) return true; // Always show avatar for first message

      const prevMsg = filteredMessages.value[index - 1];
      if (!prevMsg) return true;

      // Show avatar if sender changed or if there's a significant time gap (more than 5 minutes)
      const timeDiff = new Date(msg.sent_at) - new Date(prevMsg.sent_at);
      const fiveMinutes = 5 * 60 * 1000;

      return prevMsg.sender !== msg.sender || timeDiff > fiveMinutes;
    };

    // Track scroll position
    const onScroll = () => {
      if (!messagesContainer.value) return;

      updateIsNearBottom();

      const container = messagesContainer.value;
      const messageEls = container.querySelectorAll(".message-bubble");
      let found = false;
      for (let i = messageEls.length - 1; i >= 0; i--) {
        const el = messageEls[i];
        const rect = el.getBoundingClientRect();
        if (rect.top < container.getBoundingClientRect().top + 80) {
          // Find the corresponding message
          const msg = filteredMessages.value[i];
          if (msg) {
            const dateStr = formatDateSeparator(msg.sent_at);
            floatingDate.value = dateStr;
            found = true;
            break;
          }
        }
      }
      if (!found) floatingDate.value = null;
    };

    const toggleMessageMenu = (id) => {
      activeMessageMenu.value = activeMessageMenu.value === id ? null : id;
    };
    const closeMessageMenu = () => {
      activeMessageMenu.value = null;
    };

    // Define handleResize at the same scope level as onMounted and onUnmounted
    const handleResize = () => {
      isSmallScreen.value = window.innerWidth <= 1024;
    };

    // Watch for threadId changes and reload messages
    watch(
      () => props.threadId,
      async (newId, oldId) => {
        if (newId && newId !== oldId) {
          await fetchThreadInfo();
          await fetchMessages();
        }
      }
    );

    // Get user initial for avatar
    const getUserInitial = () => {
      // First check if we have otherParticipant from messages
      if (otherParticipant.value) {
        return otherParticipant.value.charAt(0).toUpperCase();
      }

      // If no messages, get from threadInfo
      if (threadInfo.value) {
        if (
          user.value?.account_type === "admin" &&
          threadInfo.value.jobseeker_name
        ) {
          return threadInfo.value.jobseeker_name.charAt(0).toUpperCase();
        } else if (
          user.value?.account_type === "jobseeker" &&
          threadInfo.value.admin_name
        ) {
          return threadInfo.value.admin_name.charAt(0).toUpperCase();
        }
      }

      // Fallback
      return "?";
    };

    // Get username to display
    const getUserName = () => {
      // First check if we have otherParticipant from messages
      if (otherParticipant.value) {
        return otherParticipant.value;
      }

      // If no messages, get from threadInfo
      if (threadInfo.value) {
        if (
          user.value?.account_type === "admin" &&
          threadInfo.value.jobseeker_name
        ) {
          return threadInfo.value.jobseeker_name;
        } else if (
          user.value?.account_type === "jobseeker" &&
          threadInfo.value.admin_name
        ) {
          return threadInfo.value.admin_name;
        }
      }

      // Fallback
      return "Loading...";
    };

    const editMessage = (msg) => {
      editingMessageId.value = msg.id;
      editMessageBody.value = msg.body;
    };
    const cancelEditMessage = () => {
      editingMessageId.value = null;
      editMessageBody.value = "";
    };
    const saveEditMessage = async (msg) => {
      if (!editMessageBody.value.trim()) return;
      try {
        await apiClient.patch(`/messages/messages/${msg.id}/`, {
          body: editMessageBody.value,
        });
        await fetchMessages();
        editingMessageId.value = null;
        editMessageBody.value = "";
      } catch (err) {
        alert(
          "Failed to edit message: " +
            (err.response?.data?.detail || err.message)
        );
      }
    };

    onMounted(async () => {
      if (!authStore.state.user) {
        await authStore.checkAuthStatus();
      }
      if (authStore.state.user) {
        await fetchThreadInfo();
        await fetchMessages();
      }
      document.addEventListener("click", (e) => {
        if (
          showEmojiPicker.value &&
          replyInput.value &&
          !replyInput.value.contains(e.target)
        ) {
          showEmojiPicker.value = false;
        }
        if (activeMessageMenu.value && !e.target.closest(".message-actions")) {
          activeMessageMenu.value = null;
        }
      });
      if (messagesContainer.value) {
        messagesContainer.value.addEventListener("scroll", onScroll);
      }

      // Use handleResize function defined above
      window.addEventListener("resize", handleResize);
    });

    onUnmounted(() => {
      document.removeEventListener("click", () => {});
      if (messagesContainer.value) {
        messagesContainer.value.removeEventListener("scroll", onScroll);
      }
      window.removeEventListener("resize", handleResize);
    });

    return {
      messages,
      loading,
      replyBody,
      replyToMessage,
      user: user.value,
      canReply,
      sendReply,
      formatDate,
      formatTime,
      formatDateSeparator,
      shouldShowDateSeparator,
      filteredMessages,
      onFocus,
      onBlur,
      messagesContainer,
      replyInput,
      focusReplyInput,
      showTooltip,
      showEmojiPicker,
      otherParticipant,
      isMessageFromCurrentUser,
      fetchMessages,
      isAdmin,
      repliedMessageMap,
      startReply,
      cancelReply,
      deleteMessage,
      showUnsendModal,
      messageToUnsend,
      closeUnsendModal,
      confirmUnsend,
      floatingDate,
      onScroll,
      activeMessageMenu,
      toggleMessageMenu,
      closeMessageMenu,
      shouldShowAvatar,
      isCurrentThreadArchived,
      archiveCurrentThread,
      isSmallScreen,
      getUserInitial,
      getUserName,
      isAdminProfile,
      sendingMessage,
      editingMessageId,
      editMessageBody,
      editMessage,
      cancelEditMessage,
      saveEditMessage,
    };
  },
};
</script>

<style scoped>
/* Enhanced Modern Message Thread Design */
.thread-container {
  height: 100%;
  width: 100%;
  position: relative;
  overflow: hidden;
  background: hsl(var(--background));
  display: flex;
  flex-direction: column;
}

/* Enhanced Animated Background */
.thread-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: hsl(var(--background));
  z-index: 0;
}

.floating-particles {
  display: none; /* Hide particles to match clean inbox design */
}

.particle {
  display: none; /* Hide particles to match clean inbox design */
}

.particle-1 {
  width: 60px;
  height: 60px;
  top: 20%;
  left: 10%;
  animation-delay: 0s;
}

.particle-2 {
  width: 40px;
  height: 40px;
  top: 60%;
  right: 20%;
  animation-delay: 2s;
}

.particle-3 {
  width: 80px;
  height: 80px;
  bottom: 30%;
  left: 30%;
  animation-delay: 4s;
}

.particle-4 {
  width: 30px;
  height: 30px;
  top: 40%;
  right: 40%;
  animation-delay: 1s;
}

.particle-5 {
  width: 50px;
  height: 50px;
  bottom: 10%;
  right: 10%;
  animation-delay: 3s;
}

@keyframes particleFloat {
  0%,
  100% {
    transform: translateY(0px) translateX(0px) rotate(0deg);
    opacity: 0.3;
  }
  50% {
    transform: translateY(-30px) translateX(20px) rotate(180deg);
    opacity: 0.8;
  }
}

.gradient-overlay {
  display: none; /* Hide gradient overlay to match clean inbox design */
}

@keyframes gradientShift {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 0.8;
  }
}

/* Enhanced Glassmorphism */
.glass-card {
  background: hsl(var(--card) / 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid hsl(var(--border) / 0.6);
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.glass-header {
  background: hsl(var(--card) / 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid hsl(var(--border) / 0.6);
}

.thread-main-card {
  background: hsl(var(--card));
  backdrop-filter: none;
  border: 1px solid hsl(var(--border) / 0.6);
  margin-bottom: 0 !important;
  border-bottom: none !important;
  border-radius: 0 !important;
}

/* Text color updates */
.thread-header h1,
.participant-info h1,
.empty-messages-state h3,
.message-sender {
  color: hsl(var(--foreground));
}

.empty-messages-state p,
.message-content,
.message-time,
.reply-context .reply-header,
.reply-preview .text-sm {
  color: hsl(var(--muted-foreground));
}

.message-input {
  color: hsl(var(--foreground));
  background: hsl(var(--muted) / 0.4);
  backdrop-filter: blur(15px);
  border: 1px solid hsl(var(--border) / 0.6);
  transition: all 0.3s ease;
}

.message-input:focus {
  background: hsl(var(--muted) / 0.6);
  border-color: hsl(var(--primary) / 0.5);
  box-shadow: 0 0 0 3px hsl(var(--primary) / 0.15);
}

.message-from-them {
  background: hsl(var(--muted) / 0.6);
  backdrop-filter: blur(15px);
  border: 1px solid hsl(var(--border) / 0.6);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.message-from-them .message-content,
.message-from-them .message-header,
.message-from-them .message-footer {
  color: hsl(var(--foreground));
}

.message-from-them .message-time {
  color: hsl(var(--muted-foreground));
}

.thread-header button svg {
  color: hsl(var(--muted-foreground));
}

.thread-header .archive-btn,
.thread-header .refresh-btn {
  background: hsl(var(--muted) / 0.5);
  border: 1px solid hsl(var(--border) / 0.8);
  box-shadow: none;
}

.thread-header button {
  box-shadow: none;
}

.thread-header button:hover {
  background: hsl(var(--accent) / 0.7);
  box-shadow: none;
}

.thread-header button:hover svg {
  color: hsl(var(--primary));
}

.tooltip {
  background: rgba(15, 23, 42, 0.8);
  color: white;
  backdrop-filter: blur(10px);
  border-radius: 8px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
}

.avatar-tooltip {
  background: rgba(15, 23, 42, 0.8);
  color: white;
  backdrop-filter: blur(10px);
  border-radius: 8px;
  font-size: 12px;
  z-index: 1000;
}

.empty-icon svg {
  color: hsl(var(--muted-foreground));
}

.loading-dots {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: hsl(var(--primary));
  border: 1px solid hsl(var(--primary) / 0.3);
  animation: dotBounce 1.4s ease-in-out infinite both;
}

.dot-1 {
  animation-delay: -0.32s;
}
.dot-2 {
  animation-delay: -0.16s;
}
.dot-3 {
  animation-delay: 0s;
}

@keyframes dotBounce {
  0%,
  80%,
  100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.loading-state p {
  color: hsl(var(--foreground));
}

/* Update avatar colors to match profile images */
.avatar-main,
.message-avatar {
  background: hsl(var(--primary));
  color: white;
  border: 2px solid hsl(var(--border));
  box-shadow: none;
}

.message-actions .action-menu-btn {
  background: rgba(0, 0, 0, 0.2);
  color: white;
  border: none;
  backdrop-filter: blur(8px);
}

.message-actions .action-menu-btn:hover {
  background: rgba(0, 0, 0, 0.3);
  color: white;
}

.dropdown-action {
  color: hsl(var(--foreground));
}

.empty-icon-wrapper {
  position: relative;
}

.empty-icon-bg {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: 100px;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.1) 0%,
    transparent 70%
  );
  border-radius: 50%;
  animation: iconPulse 3s ease-in-out infinite;
}

@keyframes iconPulse {
  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.3;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 0.6;
  }
}

/* Enhanced Message Bubbles */
.message-bubble {
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.message-bubble::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: inherit;
  border-radius: inherit;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.message-bubble:hover::before {
  opacity: 0.1;
}

.message-from-me {
  background: #3b82f6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  color: white;
  border: none;
}

.message-from-me .message-content,
.message-from-me .message-header,
.message-from-me .message-time,
.message-from-me .message-footer {
  color: white;
}

.message-from-them {
  background: hsl(var(--muted));
  backdrop-filter: none;
  border: 1px solid hsl(var(--border));
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.message-from-them .message-content,
.message-from-them .message-header .sender-info {
  color: hsl(var(--foreground));
}

.message-from-them .message-time {
  color: hsl(var(--muted-foreground));
}

/* Enhanced Reply Context */
.reply-context {
  background: hsl(var(--muted) / 0.6);
  backdrop-filter: blur(10px);
  border-left: 4px solid hsl(var(--primary) / 0.5);
  color: hsl(var(--foreground));
  transition: all 0.3s ease;
}

.reply-context .reply-header,
.reply-context p {
  color: hsl(var(--foreground));
}

.reply-preview {
  background: hsl(var(--muted) / 0.6);
  border-left: 4px solid hsl(var(--primary) / 0.5);
  position: relative;
}

.reply-preview .text-sm {
  color: hsl(var(--foreground));
}

.cancel-reply-btn {
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: hsl(var(--muted-foreground));
  opacity: 1;
  z-index: 2;
  transition: background 0.2s, color 0.2s;
}

.cancel-reply-btn:hover {
  background: hsl(var(--primary) / 0.15);
  color: hsl(var(--primary));
}

.cancel-reply-btn svg {
  width: 20px;
  height: 20px;
  color: inherit;
}

/* Enhanced Date Separator */
.date-badge {
  background: hsl(var(--muted) / 0.6);
  color: hsl(var(--foreground));
  backdrop-filter: blur(15px);
  border: 1px solid hsl(var(--border) / 0.6);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

/* Enhanced Actions Dropdown */
.actions-dropdown {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(25px);
  animation: dropdownSlide 0.2s ease-out;
}

@keyframes dropdownSlide {
  0% {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.dropdown-action {
  transition: all 0.2s ease;
}

.dropdown-action:hover {
  transform: translateX(4px);
}

/* Enhanced Input Area */
.input-container {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  border-radius: 0;
  background: hsl(var(--muted) / 0.4);
  border: 1px solid hsl(var(--border) / 0.7);
  margin: 0 4px 4px 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.message-input {
  color: hsl(var(--foreground));
  background: transparent;
  border: none;
  padding: 12px 0;
  min-height: 45px;
  max-height: 120px;
  backdrop-filter: none;
  box-shadow: none;
  border-radius: 0 !important;
  transition: all 0.3s ease;
}

/* Override glass-card border-radius for message input */
.message-input.glass-card,
.glass-card.message-input {
  border-radius: 0 !important;
}

.message-input:focus {
  background: transparent;
  border: none;
  box-shadow: none;
  outline: none;
  border-radius: 0 !important;
}

.message-input::placeholder {
  color: hsl(var(--muted-foreground));
}

/* Enhanced Modal */
.modal-overlay {
  backdrop-filter: blur(8px);
}

.modal-content {
  background: hsl(var(--card) / 0.9);
  backdrop-filter: blur(30px);
  border: 1px solid hsl(var(--border) / 0.6);
  animation: modalSlide 0.3s ease-out;
}

@keyframes modalSlide {
  0% {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Enhanced Scrollbar */
.messages-container::-webkit-scrollbar {
  width: 8px;
}

.messages-container::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.messages-container::-webkit-scrollbar-thumb {
  background-color: rgba(99, 102, 241, 0.4);
  border-radius: 4px;
  border: 2px solid transparent;
  background-clip: content-box;
}

.messages-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(99, 102, 241, 0.6);
}

.messages-container {
  scrollbar-width: thin;
  scrollbar-color: rgba(99, 102, 241, 0.5) rgba(255, 255, 255, 0.1);
}

/* Enhanced Transitions */
.message-slide-enter-active,
.message-slide-leave-active {
  transition: all 0.3s ease;
}
.message-slide-enter-from,
.message-slide-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.modal-overlay {
  transition: background-color 0.3s ease;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Enhanced Tooltips */
.tooltip {
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
}

.avatar-tooltip {
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  font-size: 12px;
  z-index: 1000;
}

/* Responsive Design */
@media (min-width: 600px) and (max-width: 1024px) {
  .thread-page {
    background: none !important;
    box-shadow: none !important;
    border: none !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  .input-container {
    margin-bottom: 0 !important;
  }
}
@media (max-width: 599px) {
  .thread-page {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    left: 0 !important;
    right: 0 !important;
    overflow: hidden !important;
  }
  .thread-main-card {
    width: 100% !important;
    max-width: 100% !important;
    border-radius: 0 !important;
    margin: 0 !important;
    border: none !important;
  }
}

/* Enhanced Focus States */
button:focus-visible {
  outline: 2px solid rgba(59, 130, 246, 0.5);
  outline-offset: 2px;
}

textarea:focus-visible {
  outline: none;
}

/* Admin Badge Styling */
.admin-badge {
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
}

.admin-badge svg {
  margin-right: 4px;
}

.admin-badge-reply {
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
}

.admin-badge-reply svg {
  margin-right: 3px;
}

/* Enhanced Animation Performance */
* {
  will-change: auto;
}

.message-bubble,
.avatar-main,
.send-button {
  will-change: transform;
}

/* Smooth Transitions for All Elements */
* {
  transition-property: color, background-color, border-color,
    text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter,
    backdrop-filter;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

/* Fix three-dot menu visibility */
.message-actions .action-menu-btn {
  background: rgba(0, 0, 0, 0.2);
  color: white;
  border: none;
  backdrop-filter: blur(8px);
}

.message-actions .action-menu-btn:hover {
  background: rgba(0, 0, 0, 0.3);
  color: white;
}

/* Fix message actions for message bubbles from others */
.message-from-them .message-actions .action-menu-btn {
  background: hsl(var(--muted-foreground) / 0.2);
  color: hsl(var(--foreground));
  border: 1px solid hsl(var(--border) / 0.6);
}

.message-from-them .message-actions .action-menu-btn:hover {
  background: hsl(var(--muted-foreground) / 0.35);
  color: hsl(var(--foreground));
}

/* Empty message state styling */
.empty-messages-state h3 {
  color: hsl(var(--primary));
  font-weight: 600;
}

.empty-messages-state p {
  color: hsl(var(--muted-foreground));
}

.empty-messages-state svg {
  color: hsl(var(--muted-foreground));
}

button[type="submit"].send-button {
  background: #3b82f6;
  color: white;
  border: none;
  box-shadow: none;
  transition: all 0.2s ease;
}

button[type="submit"].send-button:hover {
  background: #2563eb;
  color: white;
  transform: translateY(-2px);
  box-shadow: none;
}

.thread-page {
  background: none !important;
  box-shadow: none !important;
  border: none !important;
  margin: 0 !important;
  padding: 0 !important;
}
.input-container {
  margin-bottom: 0 !important;
  border-radius: 0 0 1.5rem 1.5rem;
}

@media (min-width: 1025px) {
  .desktop-fixed-height {
    height: 100% !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .thread-main-card {
    height: 100% !important;
    margin-bottom: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }
  .empty-messages-state {
    height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .messages-container {
    flex: 1 !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    min-height: 0 !important;
  }
}

/* Add responsive adjustments for different screen sizes */
@media (max-width: 1024px) {
  .thread-main-card {
    margin-bottom: 0 !important;
    height: auto !important;
  }
  .messages-container {
    max-height: 60vh !important;
  }
}

.input-area {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(30px);
  border-top: 1px solid rgba(100, 116, 139, 0.1);
  margin-top: 0 !important;
  border-radius: 0;
}

.input-area .input-container {
  border-radius: 0 !important;
}

.input-area .message-input {
  border-radius: 0 !important;
}

/* Add these mobile-specific message styles */
@media (max-width: 640px) {
  .message-row {
    width: 100%;
  }

  .message-group {
    max-width: calc(100% - 30px);
  }

  .message-from-me,
  .message-from-them {
    max-width: 200px !important;
  }

  .message-bubble {
    word-break: break-word;
    hyphens: auto;
  }

  .avatar-wrapper {
    flex-shrink: 0;
  }

  .message-avatar {
    flex-shrink: 0;
  }
}

/* Message Sending Animation */
.sending-animation {
  position: relative;
  width: 80px;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
}

@media (max-width: 640px) {
  .sending-animation {
    width: 60px;
    height: 60px;
  }

  .modal-content {
    padding: 1rem !important;
  }

  .modal-content h2 {
    font-size: 1.1rem !important;
  }

  .modal-content p {
    font-size: 0.85rem !important;
  }
}

.sending-circle {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: rgba(99, 102, 241, 0.8);
  animation: sending-spin 1.5s linear infinite;
}

.sending-circle:nth-child(2) {
  width: 80%;
  height: 80%;
  border-top-color: rgba(56, 189, 248, 0.8);
  animation-duration: 1.75s;
  animation-direction: reverse;
}

.sending-circle:nth-child(3) {
  width: 60%;
  height: 60%;
  border-top-color: rgba(168, 85, 247, 0.8);
  animation-duration: 2s;
}

.sending-icon {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  z-index: 2;
}

@keyframes sending-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-out forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
